'use client';
import React, {useState, useEffect, useRef} from 'react';
import {useParams, useSearchParams} from 'next/navigation';
import {Box, Stack} from '@mui/material';
import {LanguageDropdown} from '@components/LanguageDropdown';
import {logEvent} from '@utils/googleAnalytics';
import WaldorfLaunchContainer from '../WaldorfContainer';
import WaldorfLaunchButton from '../WaldorfButton';
import cn from './style.module.scss';
type Props = {
  localeOptions: {label: string; value: string}[];
  t: any;
};

export default function WaldorfHeader(props: Props) {
  const {t} = props;
  const [scrolledClass, setScrolledClass] = useState(false);
  const lastScrollY = useRef(0);
  const {lang} = useParams();
  const searchParams = useSearchParams();
  const query = Object.fromEntries(searchParams.entries());

  const handleRegisterClick = () => {
    logEvent({
      event: 'launch-register-interest-click',
      additionalInfo: {
        link_id: 'launch-register-interest-click',
        section: 'navigation',
      },
    });
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY > window.innerHeight + 20) {
        if (currentScrollY > lastScrollY.current && currentScrollY > 20) {
          setScrolledClass(false);
        } else {
          setScrolledClass(true);
        }
      } else {
        setScrolledClass(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <Box
      component={'header'}
      sx={{
        'position': 'absolute',
        'width': '100%',
        'top': 0,
        'left': 0,
        'right': 0,
        'zIndex': 5,
        'minHeight': '140px',
        'paddingTop': '2rem',
        'boxSizing': 'border-box',
        'display': 'flex',
        'flexDirection': 'column',
        ...(scrolledClass && {
          animation: 'slideIn 0.5s ease forwards',
        }),
        '@keyframes slideIn': {
          '0%': {
            transform: 'translateY(-100%)',
          },
          '100%': {
            transform: 'translateY(0)',
          },
        },
      }}>
      <WaldorfLaunchContainer>
        <Box
          component={'div'}
          sx={{
            'display': 'flex',
            'flexDirection': 'column',
            '[dir="rtl"] &': {
              direction: 'ltr',
            },
          }}>
          <Stack
            component={'div'}
            sx={{
              display: 'flex',
              flex: '1,1,auto',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: {
                xs: 'space-between',
                sm: 'space-between',
                md: 'flex-start',
              },
              gap: {
                xs: '1.5rem',
                sm: '1.5rem',
                md: '2.5rem',
              },
            }}>
            <Box
              component={'div'}
              sx={{
                flex: '0,0,auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <WaldorfLaunchButton t={t} />
            </Box>
            <Box
              component={'div'}
              sx={{
                flex: '0,0,auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <LanguageDropdown
                options={props.localeOptions}
                className={cn.waldorfLanguage}
              />
            </Box>
          </Stack>
        </Box>
      </WaldorfLaunchContainer>
    </Box>
  );
}
